// The share-link page's actions for a web app or a presentation: one round
// button, bottom right, whose menu holds "Start a new chat from here" and
// "Download PDF". Two wide buttons there covered the deck's slide arrows and
// the page's own controls. On a deck the button sits above the deck's bar.
// The menu opens when the mouse moves over the button and closes a moment
// after it leaves (Adam, 4 Nov 2026). A click or a tap toggles it, so touch
// and keyboard users get the same menu.
import React, { useEffect, useId, useRef, useState } from 'react'
import { Box, ClickAwayListener, Fab, ListItemIcon, ListItemText, MenuItem, MenuList, Paper, Popper } from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import { printWebappFrame } from '../../utils/printWebappFrame'

/** The deck's bar height (--pt-deck-bar-h in webviz/story.css) plus the gap. */
export const DECK_BAR_CLEARANCE = 56 + 16

/** How long the menu stays after the mouse leaves, so a slow move across the gap keeps it. */
export const HOVER_CLOSE_DELAY_MS = 250

export interface PageActionsFabProps {
  frameRef: React.RefObject<HTMLIFrameElement | null>
  onStartChat: () => void
  /** Why the viewer cannot start a chat from here. */
  startChatReason?: string
  /** AI access is still loading, so Start a new chat waits, disabled. */
  startChatPending?: boolean
  /** The page is a deck: keep clear of its bar. */
  isDeck?: boolean
}

export default function PageActionsFab({ frameRef, onStartChat, startChatReason, startChatPending, isDeck }: PageActionsFabProps) {
  const [open, setOpen] = useState(false)
  // Opened by a click or a key: the menu takes focus. Opened by hover: it does not.
  const [focusMenu, setFocusMenu] = useState(false)
  const fabRef = useRef<HTMLButtonElement>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const reasonId = useId()
  const menuId = useId()

  useEffect(() => () => clearTimeout(closeTimer.current), [])

  const handleClose = () => {
    clearTimeout(closeTimer.current)
    setOpen(false)
  }
  const handleMouseEnter = () => {
    clearTimeout(closeTimer.current)
    if (!open) {
      setFocusMenu(false)
      setOpen(true)
    }
  }
  const handleMouseLeave = () => {
    clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS)
  }
  const handleToggle = () => {
    clearTimeout(closeTimer.current)
    if (open && focusMenu) {
      setOpen(false)
      return
    }
    // A click while hover holds the menu open keeps it, and hands it focus.
    setFocusMenu(true)
    setOpen(true)
  }
  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      e.preventDefault()
      handleClose()
      fabRef.current?.focus()
    }
  }

  const startDisabled = !!startChatReason || !!startChatPending
  const handleStart = () => {
    if (startDisabled) return
    handleClose()
    onStartChat()
  }
  const handlePdf = () => {
    handleClose()
    printWebappFrame(frameRef.current)
  }

  return (
    <ClickAwayListener onClickAway={() => open && handleClose()}>
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-testid="page-actions"
        sx={{ position: 'absolute', right: 16, bottom: isDeck ? DECK_BAR_CLEARANCE : 16 }}
      >
        <Fab
          ref={fabRef}
          color="info"
          size="medium"
          aria-label="Page actions"
          aria-haspopup="menu"
          aria-expanded={open ? 'true' : undefined}
          aria-controls={open ? menuId : undefined}
          onClick={handleToggle}
          data-testid="page-actions-fab"
        >
          <MoreHorizIcon />
        </Fab>
        {/* In the box's own DOM (no portal), so moving from the button onto
            the menu does not count as leaving. */}
        <Popper open={open} anchorEl={fabRef.current} placement="top-end" disablePortal sx={{ zIndex: 1 }}>
          {/* The padding bridges the gap to the button, inside the hover area. */}
          <Box sx={{ pb: 1 }}>
            <Paper elevation={8}>
              <MenuList
                id={menuId}
                autoFocusItem={focusMenu}
                // A disabled action keeps its place in the arrow-key order, so a
                // keyboard or screen-reader user reaches its reason.
                disabledItemsFocusable
                onKeyDown={handleMenuKeyDown}
              >
                <MenuItem
                  onClick={handleStart}
                  disabled={startDisabled}
                  data-testid="dashboard-start-chat"
                  // The reason is the item's description, not part of its name.
                  aria-label="Start a new chat from here"
                  aria-describedby={startChatReason ? reasonId : undefined}
                  sx={startChatReason ? { alignItems: 'flex-start', '&.Mui-disabled': { opacity: 1 } } : { color: 'info.main' }}
                >
                  <ListItemIcon sx={startDisabled ? { color: 'text.disabled', mt: startChatReason ? 0.5 : 0 } : { color: 'info.main' }}>
                    <AddCommentOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  {startChatReason ? (
                    <ListItemText
                      primary="Start a new chat from here"
                      secondary={startChatReason}
                      primaryTypographyProps={{ color: 'text.disabled' }}
                      secondaryTypographyProps={{ id: reasonId, color: 'text.secondary', sx: { whiteSpace: 'normal', maxWidth: 280 } }}
                    />
                  ) : (
                    <ListItemText>Start a new chat from here</ListItemText>
                  )}
                </MenuItem>
                <MenuItem onClick={handlePdf} data-testid="webapp-download-pdf" sx={{ color: 'info.main' }}>
                  <ListItemIcon sx={{ color: 'info.main' }}>
                    <PictureAsPdfOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Download PDF</ListItemText>
                </MenuItem>
              </MenuList>
            </Paper>
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}
